import React, { useMemo } from 'react';
import { Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DiagnosticCriteriaSet } from '../types';
import { useUserInput, CriteriaSelections } from '../context/UserInputContext';

interface DiagnosticCriteriaTableProps {
  criteria: DiagnosticCriteriaSet;
  title?: string;
  subtitle?: string;
  /** Context key for storing criteria selections */
  stateKey: 'pvDiagnosisCriteria' | 'etDiagnosisCriteria' | 'mfOvertCriteria' | 'mfPrefibCriteria' | 'mfPostPvCriteria' | 'mfPostEtCriteria' | 'cmmlWho2022Criteria' | 'cmmlIcc2022Criteria';
  /** Section headings, for systems that do not call their lists major/minor */
  majorTitle?: string;
  minorTitle?: string;
  /** Evaluate diagnosis based on which major/minor criteria are met.
   *  Receives arrays of met criterion numbers (1-based). Returns { met, label }. */
  evaluateDiagnosis?: (
    majorMet: number[],
    minorMet: number[]
  ) => { met: boolean; label: string };
}

const DiagnosticCriteriaTable: React.FC<DiagnosticCriteriaTableProps> = ({
  criteria,
  title,
  subtitle,
  stateKey,
  majorTitle = 'Major Criteria',
  minorTitle = 'Minor Criteria',
  evaluateDiagnosis,
}) => {
  const { t } = useTranslation('hematology');
  const { state, update } = useUserInput();
  const selections: CriteriaSelections = state[stateKey];

  const toggle = (key: string, value: boolean) => {
    update(stateKey, {
      ...selections,
      [key]: selections[key] === value ? null : value,
    });
  };

  const { majorMet, minorMet, hasAnySelection } = useMemo(() => {
    const major = criteria.majorCriteria
      .filter((c) => selections[`major-${c.number}`] === true)
      .map((c) => c.number);
    const minor = criteria.minorCriteria
      .filter((c) => selections[`minor-${c.number}`] === true)
      .map((c) => c.number);

    return {
      majorMet: major,
      minorMet: minor,
      hasAnySelection: Object.values(selections).some(
        (v) => v !== null && v !== undefined
      ),
    };
  }, [selections, criteria]);

  const diagnosisResult = useMemo(() => {
    if (!evaluateDiagnosis || !hasAnySelection) return null;
    return evaluateDiagnosis(majorMet, minorMet);
  }, [evaluateDiagnosis, hasAnySelection, majorMet, minorMet]);

  const renderCriterionRow = (
    type: 'major' | 'minor',
    criterion: { number: number; text: string; notes?: string }
  ) => {
    const key = `${type}-${criterion.number}`;
    const value = selections[key];
    const isMet = value === true;
    const isNotMet = value === false;

    return (
      <article
        key={key}
        className={`criterion-item ${isMet ? 'is-met' : ''} ${isNotMet ? 'is-not-met' : ''}`}
      >
        <div className="criterion-item__body">
          <span className="criterion-item__number">{criterion.number}</span>
          <div className="criterion-item__content" dir="ltr">
            <p className="criterion-item__text">{criterion.text}</p>
            {criterion.notes && (
              <p className="criterion-item__notes">{criterion.notes}</p>
            )}
          </div>
        </div>

        {evaluateDiagnosis && (
          <div className="yesno yesno--compact" role="group">
            <button
              type="button"
              className={`yesno__btn yesno__btn--yes ${isMet ? 'is-active' : ''}`}
              aria-pressed={isMet}
              aria-label={t('criterionMet')}
              onClick={() => toggle(key, true)}
            >
              <Check size={16} strokeWidth={2.5} />
              <span>{t('met')}</span>
            </button>
            <button
              type="button"
              className={`yesno__btn yesno__btn--no ${isNotMet ? 'is-active' : ''}`}
              aria-pressed={isNotMet}
              aria-label={t('criterionNotMet')}
              onClick={() => toggle(key, false)}
            >
              <X size={16} strokeWidth={2.5} />
              <span>{t('no')}</span>
            </button>
          </div>
        )}
      </article>
    );
  };

  return (
    <div className="diagnostic-criteria">
      {title && (
        <header className="diagnostic-criteria__header">
          <h3 className="diagnostic-criteria__title">{title}</h3>
          {subtitle && (
            <p className="diagnostic-criteria__subtitle">{subtitle}</p>
          )}
        </header>
      )}

      <div className="diagnosis-rule">
        <strong>{t('diagnosisRule')}</strong> {criteria.diagnosisRule}
      </div>

      <section className="criteria-section">
        <h4 className="criteria-section__title criteria-section__title--major">
          <span>{majorTitle}</span>
          {evaluateDiagnosis && (
            <span className="criteria-section__count">
              {majorMet.length}/{criteria.majorCriteria.length}
            </span>
          )}
        </h4>
        <div className="criteria-list">
          {criteria.majorCriteria.map((c) => renderCriterionRow('major', c))}
        </div>
      </section>

      {criteria.minorCriteria.length > 0 && (
        <section className="criteria-section">
          <h4 className="criteria-section__title criteria-section__title--minor">
            <span>{minorTitle}</span>
            {evaluateDiagnosis && (
              <span className="criteria-section__count">
                {minorMet.length}/{criteria.minorCriteria.length}
              </span>
            )}
          </h4>
          <div className="criteria-list">
            {criteria.minorCriteria.map((c) => renderCriterionRow('minor', c))}
          </div>
        </section>
      )}

      {diagnosisResult && (
        <div
          className={`diagnosis-result ${diagnosisResult.met ? 'tone--green' : 'tone--red'}`}
          role="status"
        >
          {diagnosisResult.met ? (
            <Check size={20} strokeWidth={3} />
          ) : (
            <X size={20} strokeWidth={3} />
          )}
          <span>{diagnosisResult.label}</span>
        </div>
      )}
    </div>
  );
};

export default DiagnosticCriteriaTable;
